import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertKind,
  AlertLogItem,
  AlertPreview,
  AlertSettings,
  AlertSettingsResponse,
  EditableSettings,
  NumericSetting,
  PreviewRow,
  TestResult,
  adminAlertsApi,
} from '../services/adminAlertsApi';
import { RelativeTime } from '../components/RelativeTime';
import { SectionTitle, fmtMs, fmtNum, fmtPct } from '../components/performance/common';
import { usePageTitle } from '../utils/pageTitle';

const EMAIL_RE = /^[a-z0-9._%+'-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

const NUMERIC: NumericSetting[] = [
  'digest_hour_utc',
  'window_minutes',
  'min_runs',
  'error_rate_pct',
  'timeout_rate_pct',
  'p95_deadline_pct',
  'p95_ms',
  'cooldown_minutes',
  'baseline_days',
  'slowdown_pct',
  'slowdown_min_ms',
  'fallback_rate_pct',
  'traffic_drop_pct',
];

type Draft = Omit<EditableSettings, NumericSetting> & Record<NumericSetting, string>;
type Limits = AlertSettingsResponse['limits'];

const KIND_LABELS: Record<AlertKind, string> = {
  platform_function: 'Platform function',
  model_down: 'Model down',
  model_recovered: 'Model recovered',
  digest: 'Daily digest',
  test: 'Test',
};

const STATUS: Record<PreviewRow['status'], { label: string; color: 'error' | 'warning' | 'success' | 'default' }> = {
  new: { label: 'Would alert', color: 'error' },
  reminder: { label: 'Would re-send', color: 'error' },
  cooldown: { label: 'Firing (cooldown)', color: 'warning' },
  waiting: { label: 'Firing, few runs', color: 'warning' },
  recovered: { label: 'Would resolve', color: 'success' },
  quiet: { label: 'Would resolve (quiet)', color: 'success' },
  too_few_runs: { label: 'Too few runs', color: 'default' },
  ok: { label: 'OK', color: 'default' },
};

const toDraft = (s: AlertSettings): Draft => {
  const { updated_by: _by, updated_at: _at, ...rest } = s;
  const nums = Object.fromEntries(NUMERIC.map(k => [k, s[k] === null ? '' : String(s[k])])) as Record<NumericSetting, string>;
  return { ...rest, ...nums };
};

const toPayload = (d: Draft): EditableSettings => {
  const nums = Object.fromEntries(NUMERIC.map(k => [k, d[k].trim() === '' ? null : Number(d[k])])) as Record<NumericSetting, number>;
  return { ...d, ...nums, p95_ms: d.p95_ms.trim() === '' ? null : Number(d.p95_ms) };
};

function numberErrors(d: Draft, limits: Limits): Partial<Record<NumericSetting, string>> {
  const out: Partial<Record<NumericSetting, string>> = {};
  for (const k of NUMERIC) {
    const raw = d[k].trim();
    if (raw === '') {
      if (k !== 'p95_ms') out[k] = 'Required';
      continue;
    }
    const n = Number(raw);
    const { min, max, integer } = limits[k];
    if (Number.isNaN(n)) out[k] = 'Not a number';
    else if (integer && !Number.isInteger(n)) out[k] = 'Whole number';
    else if (n < min || n > max) out[k] = `${fmtNum(min)}–${fmtNum(max)}`;
  }
  return out;
}

const localHour = (utcHour: string) => {
  const h = Number(utcHour);
  if (utcHour.trim() === '' || !Number.isInteger(h) || h < 0 || h > 23) return null;
  const d = new Date();
  d.setUTCHours(h, 0, 0, 0);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} your time${zone ? ` (${zone})` : ''}`;
};

const functionPath = (r: PreviewRow) => {
  if (!r.function_id) return null;
  const params = new URLSearchParams({ tab: 'performance', customer_id: r.customer_id });
  if (r.customer_tag) params.set('customer_tag', r.customer_tag);
  return `/dashboard/function-runs/${r.function_id}?${params}`;
};

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : 'Request failed');

const Toggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ label, checked, onChange, disabled }) => (
  <FormControlLabel control={<Switch checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />} label={label} />
);

const RecipientsInput: React.FC<{ value: string[]; max: number; onChange: (v: string[]) => void }> = ({ value, max, onChange }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const parts = text
      .split(/[\s,;]+/)
      .map(p => p.trim().toLowerCase())
      .filter(Boolean);
    if (!parts.length) return;
    const bad = parts.filter(p => p.length > 254 || !EMAIL_RE.test(p));
    if (bad.length) {
      setError(`Not a valid email: ${bad.join(', ')}`);
      return;
    }
    const next = [...value];
    parts.forEach(p => !next.includes(p) && next.push(p));
    if (next.length > max) {
      setError(`At most ${max} recipients`);
      return;
    }
    onChange(next);
    setText('');
    setError(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, minHeight: 32 }}>
        {value.length === 0 ? (
          <Typography variant="body2" color="error.main">
            No recipients: nothing will be emailed.
          </Typography>
        ) : (
          value.map(email => <Chip key={email} label={email} onDelete={() => onChange(value.filter(e => e !== email))} />)
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          label="Add email"
          placeholder="name@passiolife.com"
          value={text}
          error={!!error}
          helperText={error || 'Press Enter to add. Paste several separated by commas.'}
          onChange={e => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          sx={{ minWidth: 320 }}
        />
        <Button variant="outlined" onClick={add} disabled={!text.trim()} sx={{ mt: 0.25 }}>
          Add
        </Button>
      </Box>
    </Box>
  );
};

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
    {children}
  </Typography>
);

const PreviewTable: React.FC<{ preview: AlertPreview }> = ({ preview }) => (
  <Box>
    <Alert severity={preview.inactive_reason ? 'info' : preview.would_email ? 'warning' : 'success'} sx={{ mb: 1.5 }}>
      {preview.inactive_reason
        ? `${preview.inactive_reason} With these thresholds a tick would decide as below.`
        : preview.would_email
          ? `A tick now would send one email: "${preview.subject}"`
          : 'A tick now would not send anything.'}
      <Typography variant="caption" component="div" color="text.secondary">
        Last {preview.window_minutes} minutes, checked {new Date(preview.evaluated_at).toLocaleTimeString()}. p95 and median count successful runs; &quot;normal&quot; is the
        same function over the previous days.
      </Typography>
    </Alert>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Function</TableCell>
            <TableCell align="right">Runs</TableCell>
            <TableCell align="right">Errors</TableCell>
            <TableCell align="right">Timed out</TableCell>
            <TableCell align="right">Near deadline</TableCell>
            <TableCell align="right">p95 / limit</TableCell>
            <TableCell align="right">Median</TableCell>
            <TableCell align="right">Backup model</TableCell>
            <TableCell>Decision</TableCell>
            <TableCell>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preview.rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                No platform function runs in the window.
              </TableCell>
            </TableRow>
          ) : (
            preview.rows.map(r => {
              const codes = new Set(r.reasons.map(x => x.code));
              const path = functionPath(r);
              const status = STATUS[r.status];
              return (
                <TableRow key={r.key}>
                  <TableCell>
                    {path ? (
                      <Link component={RouterLink} to={path} variant="body2" underline="hover" sx={{ fontWeight: 500 }}>
                        {r.function_name}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {r.function_name}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {[r.platform_tool, r.customer_tag || r.customer_id, r.custom ? 'custom' : null].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ color: codes.has('traffic_drop') ? 'error.main' : undefined }}>
                    {fmtNum(r.runs)}
                    {r.normal_runs !== null && <Sub>normal {fmtNum(Math.round(r.normal_runs))}</Sub>}
                  </TableCell>
                  <TableCell align="right" sx={{ color: codes.has('error_rate') ? 'error.main' : undefined }}>
                    {fmtPct(r.error_rate)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: codes.has('timeouts') ? 'error.main' : undefined }}>
                    {fmtPct(r.timeout_rate)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: codes.has('near_deadline') ? 'error.main' : undefined }}>
                    {r.go_timeout_s ? fmtPct(r.near_deadline_rate) : '–'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: codes.has('p95_deadline') || codes.has('p95_ms') ? 'error.main' : undefined }}>
                    {fmtMs(r.p95_ms)} / {r.p95_limit_ms ? fmtMs(r.p95_limit_ms) : 'off'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: codes.has('slowdown') ? 'error.main' : undefined }}>
                    {fmtMs(r.p50_ms)}
                    {r.normal_p50_ms !== null && <Sub>normal {fmtMs(r.normal_p50_ms)}</Sub>}
                  </TableCell>
                  <TableCell align="right" sx={{ color: codes.has('fallback') ? 'error.main' : undefined }}>
                    {fmtPct(r.fallback_rate)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.note || (r.action && !r.emailed ? 'Logged, not emailed (recovery emails are off)' : '')}>
                      <Chip size="small" label={status.label} color={status.color} variant={r.action ? 'filled' : 'outlined'} />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.reasons.map(x => x.label).join('; ') || r.note}</Typography>
                    {r.top_errors.length > 0 && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Top errors: {r.top_errors.map(e => `${e.label} (${e.runs})`).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const sentChip = (item: AlertLogItem) => {
  if (item.sent_ok === true) return <Chip size="small" color="success" variant="outlined" label="Sent" />;
  if (item.sent_ok === false) {
    const failed = item.details.failed;
    return (
      <Tooltip title={failed?.length ? `Failed: ${failed.join(', ')}` : 'SendGrid did not accept it'}>
        <Chip size="small" color="error" label="Failed" />
      </Tooltip>
    );
  }
  return <Chip size="small" variant="outlined" label={item.details.emailed === false ? 'Not emailed' : 'Pending'} />;
};

export const AdminAlertSettings: React.FC = () => {
  usePageTitle('Alert emails');
  const { getIdToken } = useAuth();
  const api = useMemo(() => adminAlertsApi(getIdToken), [getIdToken]);
  const [loaded, setLoaded] = useState<AlertSettingsResponse | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<AlertPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [log, setLog] = useState<AlertLogItem[] | null>(null);
  const [logKind, setLogKind] = useState<AlertKind | ''>('');
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .settings()
      .then(res => {
        if (cancelled) return;
        setLoaded(res);
        setDraft(toDraft(res.settings));
      })
      .catch(e => !cancelled && setError(errorMessage(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, reload]);

  const loadLog = useCallback(() => {
    setLogLoading(true);
    setLogError(null);
    api
      .log(100, logKind || undefined)
      .then(res => setLog(res.items))
      .catch(e => setLogError(errorMessage(e)))
      .finally(() => setLogLoading(false));
  }, [api, logKind]);

  useEffect(() => {
    loadLog();
  }, [loadLog, reload]);

  const errors = useMemo(() => (draft && loaded ? numberErrors(draft, loaded.limits) : {}), [draft, loaded]);
  const hasErrors = Object.keys(errors).length > 0;
  const dirty = useMemo(
    () => !!draft && !!loaded && JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(toDraft(loaded.settings))),
    [draft, loaded],
  );

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft(d => (d ? { ...d, [key]: value } : d));
    setSaveMessage(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await api.save(toPayload(draft));
      setLoaded(res);
      setDraft(toDraft(res.settings));
      setSaveMessage('Saved. Every server picks it up within a minute.');
    } catch (e) {
      setSaveMessage(`Not saved: ${errorMessage(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!draft) return;
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      setTestResult(await api.test(draft.recipients));
      loadLog();
    } catch (e) {
      setTestError(errorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  const runPreview = async () => {
    if (!draft) return;
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(await api.preview(toPayload(draft)));
    } catch (e) {
      setPreviewError(errorMessage(e));
    } finally {
      setPreviewing(false);
    }
  };

  const num = (key: NumericSetting, label: string, unit?: string, helper?: string, width = 170) => (
    <TextField
      size="small"
      type="number"
      label={label}
      value={draft?.[key] ?? ''}
      onChange={e => set(key, e.target.value)}
      error={!!errors[key]}
      helperText={errors[key] || helper}
      disabled={!draft}
      sx={{ width }}
      slotProps={{
        input: unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined,
        htmlInput: loaded ? { min: loaded.limits[key].min, max: loaded.limits[key].max, step: loaded.limits[key].integer ? 1 : 0.5 } : undefined,
      }}
    />
  );

  const off = !draft?.enabled;
  const settings = loaded?.settings;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Alert emails</Typography>
          <Typography variant="body2" color="text.secondary">
            Who gets emailed, and when: models going down, the daily reliability digest, and platform functions failing, getting slow, leaning on backup models, or close to the platform&apos;s timeouts.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {loaded?.environment && <Chip label={`Environment: ${loaded.environment}`} variant="outlined" />}
          <IconButton onClick={() => setReload(r => r + 1)} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loaded && !loaded.saved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These settings aren&apos;t saved in this environment&apos;s database yet, so defaults apply and platform function alerts are off. Save to turn them on.
        </Alert>
      )}

      {draft && loaded && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionTitle title="Recipients" help={`Everyone here gets every alert email from ${loaded.environment || 'this environment'}. Up to ${loaded.limits.max_recipients}.`} />
            <RecipientsInput value={draft.recipients} max={loaded.limits.max_recipients} onChange={v => set('recipients', v)} />
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap', mt: 2 }}>
              <Box>
                <Toggle label="Send alert emails" checked={draft.enabled} onChange={v => set('enabled', v)} />
                <Typography variant="caption" color="text.secondary" component="div">
                  Master switch. Off: nothing is emailed; alerts still show in the portal.
                </Typography>
              </Box>
              <TextField
                size="small"
                label="Subject prefix"
                value={draft.subject_prefix}
                onChange={e => set('subject_prefix', e.target.value)}
                error={draft.subject_prefix.trim().length > loaded.limits.max_prefix}
                helperText={`The environment is added: "${(() => {
                  const p = draft.subject_prefix.trim();
                  const env = loaded.environment;
                  if (!env) return p;
                  return p.endsWith(']') ? `${p.slice(0, -1)} ${env}]` : `${p} [${env}]`.trim();
                })()} Platform alert: …"`}
                sx={{ minWidth: 280 }}
                slotProps={{ htmlInput: { maxLength: loaded.limits.max_prefix } }}
              />
            </Box>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Paper variant="outlined" sx={{ p: 2, opacity: off ? 0.6 : 1 }}>
              <SectionTitle title="Model alerts" help="When a model has been down for 3 minutes in a row, and when it recovers after that; at most once per model per hour each way. Calls cut short by the caller's own time limit don't count as model failures." />
              <Toggle label="Email when a model goes down or recovers" checked={draft.model_alerts_enabled} onChange={v => set('model_alerts_enabled', v)} />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, opacity: off ? 0.6 : 1 }}>
              <SectionTitle title="Daily digest" help="Everything on the Platform Routes alerts list, once a day." />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Toggle label="Send the daily digest" checked={draft.digest_enabled} onChange={v => set('digest_enabled', v)} />
                {num('digest_hour_utc', 'Hour (UTC)', ':00', localHour(draft.digest_hour_utc) ?? undefined, 220)}
              </Box>
            </Paper>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, opacity: off ? 0.6 : 1 }}>
            <SectionTitle
              title="Platform function alerts"
              help="Checked every minute for the built-in platform_* functions and functions marked as platform functions. All functions that cross a threshold on the same check go out in one email."
            />
            <Toggle label="Email when a platform function crosses a threshold" checked={draft.platform_alerts_enabled} onChange={v => set('platform_alerts_enabled', v)} />
            <Box sx={{ opacity: draft.platform_alerts_enabled ? 1 : 0.6 }}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Window
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {num('window_minutes', 'Look back', 'min', 'Stats cover this many minutes')}
                {num('min_runs', 'Minimum runs', 'runs', 'Fewer runs: not judged')}
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Alert when
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {num('error_rate_pct', 'Error rate ≥', '%', 'Failed runs')}
                {num('timeout_rate_pct', 'Timeouts ≥', '%', 'Timed out, or near the deadline', 200)}
                {num('p95_deadline_pct', 'p95 ≥', '% of deadline', "Of the platform's Go deadline", 200)}
                {num('p95_ms', 'p95 ≥ (no deadline)', 'ms', 'Functions without a deadline; empty = off', 220)}
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                Compared with normal
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                &quot;Normal&quot; is the same function over the previous days, not counting the window above.
              </Typography>
              <Box sx={{ mb: 2 }}>{num('baseline_days', 'Normal = previous', 'days', 'Slowdown and traffic checks', 220)}</Box>
              <Stack spacing={2}>
                <Box>
                  <Toggle label="Slower than normal" checked={draft.slowdown_enabled} onChange={v => set('slowdown_enabled', v)} />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    The median (typical) successful run got slower than usual, e.g. &quot;median 3.4s vs normal 1.9s (+79%)&quot;. Catches a function getting slow
                    long before it hits a deadline. Needs the minimum runs in the window and at least {Math.max(Number(draft.min_runs) || 0, 50)} runs of
                    history.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', opacity: draft.slowdown_enabled ? 1 : 0.6 }}>
                    {num('slowdown_pct', 'Median slower by ≥', '%', 'Than normal', 200)}
                    {num('slowdown_min_ms', 'and by at least', 'ms', 'Ignores tiny changes on fast functions', 240)}
                  </Box>
                </Box>
                <Box>
                  <Toggle label="Answered by backup models" checked={draft.fallback_enabled} onChange={v => set('fallback_enabled', v)} />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Share of runs where the chosen model failed and a backup model answered instead. A rising share usually means a provider is
                    unstable, even if every run still succeeds.
                  </Typography>
                  <Box sx={{ opacity: draft.fallback_enabled ? 1 : 0.6 }}>{num('fallback_rate_pct', 'Backup answers ≥', '%', 'Of runs in the window', 200)}</Box>
                </Box>
                <Box>
                  <Toggle label="Traffic drop" checked={draft.traffic_drop_enabled} onChange={v => set('traffic_drop_enabled', v)} />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Far fewer runs than this time of day on previous days, including none at all. Can mean an app or upstream service stopped calling us.
                    Only for functions that normally get at least the minimum runs per window. Off by default: quiet nights and launches can trip it.
                  </Typography>
                  <Box sx={{ opacity: draft.traffic_drop_enabled ? 1 : 0.6 }}>{num('traffic_drop_pct', 'Runs below', '% of normal', 'Same window on previous days', 220)}</Box>
                </Box>
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Repeats and recovery
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {num('cooldown_minutes', 'Cooldown', 'min', 'Re-send if still firing after', 200)}
                <Box>
                  <Toggle label="Email when it recovers" checked={draft.recovery_emails} onChange={v => set('recovery_emails', v)} />
                  <Typography variant="caption" color="text.secondary" component="div">
                    Once, when it&apos;s back under every threshold (or too quiet to judge for a full window).
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ p: 2, position: 'sticky', bottom: 0, zIndex: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', bgcolor: 'background.paper' }}
          >
            <Button variant="contained" onClick={save} disabled={!dirty || hasErrors || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={() => setDraft(toDraft(loaded.settings))} disabled={!dirty || saving}>
              Discard changes
            </Button>
            <Tooltip title="Sends to the recipients above, saved or not">
              <span>
                <Button variant="outlined" onClick={sendTest} disabled={testing || draft.recipients.length === 0}>
                  {testing ? 'Sending…' : 'Send test email'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Checks platform functions now with the thresholds above, without sending anything">
              <span>
                <Button variant="outlined" onClick={runPreview} disabled={previewing || hasErrors}>
                  {previewing ? 'Checking…' : 'Preview what would alert now'}
                </Button>
              </span>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            {saveMessage ? (
              <Typography variant="body2" color={saveMessage.startsWith('Not saved') ? 'error.main' : 'success.main'}>
                {saveMessage}
              </Typography>
            ) : dirty ? (
              <Typography variant="body2" color="warning.main">
                Unsaved changes
              </Typography>
            ) : settings?.updated_at ? (
              <Typography variant="body2" color="text.secondary">
                Last saved by {settings.updated_by || 'unknown'} <RelativeTime value={settings.updated_at} />
              </Typography>
            ) : null}
          </Paper>

          {(testResult || testError) && (
            <Alert severity={testError ? 'error' : testResult?.sent_ok ? 'success' : 'warning'} 
              onClose={() => {
                setTestResult(null);
                setTestError(null);
              }}
            >
              {testError ||
                (testResult && (
                  <>
                    Test email &quot;{testResult.subject}&quot;:{' '}
                    {testResult.results.map(r => `${r.email} ${r.ok ? 'sent' : 'failed'}`).join(', ')}
                  </>
                ))}
            </Alert>
          )}

          {(preview || previewError) && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle title="Preview" help="What the next check would do with the thresholds above. Nothing is sent or recorded." />
              {previewError ? <Alert severity="error">{previewError}</Alert> : preview && <PreviewTable preview={preview} />}
            </Paper>
          )}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <SectionTitle
          title="Recent alerts"
          help="Every alert email from this environment, kept 90 days. A batched platform alert has one row per function."
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Kind</InputLabel>
                <Select label="Kind" value={logKind} onChange={e => setLogKind(e.target.value as AlertKind | '')}>
                  <MenuItem value="">All</MenuItem>
                  {(Object.keys(KIND_LABELS) as AlertKind[]).map(k => (
                    <MenuItem key={k} value={k}>
                      {KIND_LABELS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={loadLog} disabled={logLoading}>
                {logLoading ? <CircularProgress size={22} /> : <RefreshIcon />}
              </IconButton>
            </Box>
          }
        />
        {logError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {logError}
          </Alert>
        )}
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>What</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell align="right">Recipients</TableCell>
                <TableCell>Sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!log || log.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    {log ? 'No alerts yet' : 'Loading…'}
                  </TableCell>
                </TableRow>
              ) : (
                log.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <RelativeTime value={item.created_at} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={item.state === 'resolved' && item.kind === 'platform_function' ? 'Platform resolved' : KIND_LABELS[item.kind] || item.kind}
                        color={item.state === 'firing' ? 'error' : item.state === 'resolved' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {item.key || '–'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {item.subject || '–'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={item.recipients.join(', ')}>
                        <span>{item.recipients.length}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{sentChip(item)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AdminAlertSettings;
